import { PageLayout } from '../components/layout/PageLayout';

export default function PrivacyPolicy() {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>

        <div className="space-y-6 text-gray-300">
          <p className="text-sm text-gray-400">
            <strong>Effective Date:</strong> November 4, 2025<br />
            <strong>Product:</strong> Social Pack ("Social Pack", "the Service")<br />
            <strong>Company:</strong> Throttle Labs, Inc. ("Throttle Labs", "we", "us", "our")<br />
            <strong>Contact:</strong> <a href="mailto:privacy@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">privacy@throttlelabs.ai</a> | 624 E Beale St, Kingman, AZ 86401
          </p>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1) Overview</h2>
            <p>
              Social Pack helps real estate and mortgage professionals create brand-consistent social images from a reference headshot, scenes, and styles. This Privacy Policy explains what we collect, how we use and share it, and your choices. If anything here conflicts with our Terms of Service, this policy controls for privacy matters.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2) Information we collect</h2>

            <h3 className="text-xl font-semibold text-white mb-2">A. Account & Contact Info</h3>
            <p className="mb-4">
              Name, email, password (hashed), role/company, time zone. Optional: phone, social handles, brand colors.
            </p>

            <h3 className="text-xl font-semibold text-white mb-2">B. Content You Provide</h3>
            <p className="mb-4">
              Reference images/headshots (you must have rights/consent), prompts, captions, brand palettes, uploaded assets, and any outputs you choose to save.
            </p>

            <h3 className="text-xl font-semibold text-white mb-2">C. Usage & Device Data</h3>
            <p className="mb-4">
              App events and logs, IP address, browser/OS, device identifiers, cookies, coarse geolocation inferred from IP (city/region).
            </p>

            <h3 className="text-xl font-semibold text-white mb-2">D. Billing Data</h3>
            <p className="mb-4">
              Billing name/email and transaction metadata processed by our payment processor (e.g., Stripe). We do not store full card numbers.
            </p>

            <h3 className="text-xl font-semibold text-white mb-2">E. Support & Surveys</h3>
            <p className="mb-4">
              Messages to support, feedback, survey responses.
            </p>

            <p className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <strong>Face data note:</strong> Social Pack uses headshots only to maintain likeness in images you request. We do not perform identity verification, and we do not sell or share biometric data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3) How we use data</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>Provide, operate, and secure the Service.</li>
              <li>Generate images and captions per your instructions.</li>
              <li>Personalize features (e.g., seasonal/holiday scenes).</li>
              <li>Communicate about updates, billing, security, and support.</li>
              <li>Improve quality via analytics and de-identified aggregates (see Model & Vendor Use).</li>
              <li>Prevent fraud/abuse and comply with law.</li>
            </ul>
            <p className="mt-4">
              <strong>Legal bases (GDPR):</strong> Contract (Art. 6(1)(b)), Legitimate Interests (Art. 6(1)(f)) for security/product improvement, Consent (Art. 6(1)(a)) for marketing/optional features, Legal Obligation (Art. 6(1)(c)).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4) AI models & vendors</h2>
            <p>
              We use third-party AI and infrastructure providers as processors/sub-processors (e.g., Google, OpenAI, AWS). They process data only under our instructions, apply appropriate security, and are not permitted to use your inputs/outputs to train public models unless you explicitly opt in and vendor terms allow.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5) Face data handling</h2>
            <p className="mb-4">
              <strong>Purpose:</strong> Preserve the person's likeness in your requested outputs.
            </p>
            <p className="mb-4">
              <strong>Processing:</strong> The system may create temporary numeric representations (embeddings) from your headshot to render images.
            </p>
            <p className="mb-4">
              <strong>Retention:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4">
              <li><strong>Ephemeral (default):</strong> Reference images and derived embeddings are deleted after generation or session timeout (e.g., within 24 hours).</li>
              <li><strong>Library mode (opt-in):</strong> If you save a "profile," we store the headshot and embeddings until you delete them or close your account.</li>
            </ul>
            <p className="mb-4">
              No sale or sharing of face data for cross-context behavioral advertising.
            </p>
            <p>
              You confirm you have rights/consent for any person pictured.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6) Sharing your information</h2>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Service providers:</strong> hosting, AI inference, analytics, email, payments, customer support.</li>
              <li><strong>Legal/compliance:</strong> to comply with law, enforce terms, protect rights, or respond to lawful requests.</li>
              <li><strong>Business transfers:</strong> in a merger, acquisition, or sale of assets, we may transfer data with notice.</li>
            </ul>
            <p className="mt-4">
              We do not sell personal information. We do not share personal information for targeted advertising.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7) International transfers</h2>
            <p>
              Data may be processed in the U.S. and other countries. We use appropriate safeguards (e.g., Standard Contractual Clauses).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8) Retention</h2>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Account data:</strong> for the life of your account and a reasonable period after closure (or sooner on verified request) for security/audit/legal.</li>
              <li><strong>Logs/analytics:</strong> retained in aggregated or de-identified form.</li>
              <li><strong>Face data:</strong> per Section 5.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9) Security</h2>
            <p>
              Encryption in transit, access controls, least-privilege, monitoring, backups, and vendor diligence. No method is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10) Your rights</h2>
            <p className="mb-4">
              <strong>GDPR/UK:</strong> access, rectification, erasure, restriction, portability, objection, and complaint to a supervisory authority.
            </p>
            <p className="mb-4">
              <strong>California (CCPA/CPRA):</strong> know, delete, correct, limit use of sensitive info, and opt-out of "sharing." We do not "sell" personal info.
            </p>
            <p>
              <strong>How to exercise:</strong> email <a href="mailto:privacy@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">privacy@throttlelabs.ai</a>. We may need to verify your request.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11) Cookies</h2>
            <p>
              We use essential cookies (login/session) and performance analytics. Manage via browser settings or in-app controls.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12) Children</h2>
            <p>
              Not for children under 16. Do not upload images of minors without parental/guardian consent and a lawful basis. We delete such data if discovered.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13) Marketing</h2>
            <p>
              With consent, we send updates and tips. Unsubscribe any time in emails or app settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14) Model improvement</h2>
            <p>
              We may use aggregated, de-identified usage data and prompt telemetry to improve features. We do not use identifiable headshots or saved outputs to train public models without your explicit opt-in.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15) Changes</h2>
            <p>
              We'll update this page and change the Effective Date. Material changes will be notified in-app or by email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">16) Contact</h2>
            <p>
              <a href="mailto:privacy@throttlelabs.ai" className="text-throttle-cyan-500 hover:underline">privacy@throttlelabs.ai</a><br />
              624 E Beale St, Kingman, AZ 86401
            </p>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}
